/**
 * Legacy bridge — Project Notes domain.
 *
 * Owns Project-Notes-specific window-global rewires: notably
 * `exposeGlobalAddToNotes` (which replaces `window.addCommentToProjectNotes`
 * with a React handler and neutralizes `window.getGraphInfoToggle` to a
 * no-op). Also provides Notes-flow access to `messageFactory`,
 * `posthog`, `hj`, and the event emitter.
 *
 * Sibling: `views/react/components/LeftMenuMigration/legacyBridge.js` —
 * cross-cutting helpers used app-wide (userFactory, graphFactory,
 * isGraphOnlyMode, share-link helpers). Distinct from this file by domain;
 * functions ARE duplicated for `emitEvent`/`onEvent` and that's a
 * known consolidation candidate.
 *
 * When flags.new_3d_graph is true, the new React stores (useGraphStore,
 * useZustand) are the source of truth. When false, legacy window.* globals
 * are used.
 */

import { useBootstrapStore } from '../../stores/useBootstrapStore'

// ─── Feature flag check ──────────────────────────────────────────────

function isNewMode() {
    const flags = useBootstrapStore.getState().flags
    return flags?.new_3d_graph === true
}

// ─── Event emitter ───────────────────────────────────────────────────

export function emitEvent(eventName, payload) {
    const emitter = window.myEventEmitter
    if (emitter?.emit) {
        emitter.emit(eventName, payload)
    }
}

export function onEvent(eventName, handler) {
    const emitter = window.myEventEmitter
    if (!emitter) return () => {}
    emitter.on(eventName, handler)
    return () => emitter.removeListener(eventName, handler)
}

// ─── User messages ───────────────────────────────────────────────────

export function showMessage(text, duration = 4000) {
    if (window.messageFactory?.showMessage) {
        window.messageFactory.showMessage(text, duration)
    }
}

// ─── Analytics tracking ──────────────────────────────────────────────

export function trackEvent(eventName, properties) {
    if (window.hj) {
        window.hj('event', eventName)
    }
    if (window.posthog?.capture) {
        window.posthog.capture(eventName, properties)
    }
}

// ─── Expose globals for legacy AI chat widget ────────────────────────

export function exposeGlobalAddToNotes(handler) {
    window.addCommentToProjectNotes = handler
    // Neutralize old EJS toggle so CopyToProjectNotesButton doesn't open old panel
    window.getGraphInfoToggle = () => {}
}
